/**
 * CV Upload Component - Upload and manage CV for CV Review interviews
 *
 * Copyright (c) 2024 Technical Interviewer Attempt. All Rights Reserved.
 * Proprietary and Confidential. Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useState, useCallback, useEffect } from 'react';

// For CV RAG operations, always use the deployed API (Pinecone is configured there)
// This ensures RAG indexing works even during local development
const API_URL =
  import.meta.env.VITE_RAG_API_URL ||
  import.meta.env.VITE_API_URL ||
  'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

interface CVUploadProps {
  user: { username?: string; email?: string };
  getToken: () => string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface CVStatus {
  has_cv: boolean;
  vector_count?: number;
  index_name?: string;
  message?: string;
}

const CVUpload: React.FC<CVUploadProps> = ({
  user,
  getToken,
  onClose,
  onSuccess,
}) => {
  const [cvText, setCvText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [cvStatus, setCvStatus] = useState<CVStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState<boolean>(true);

  const username = user?.username || user?.email?.split('@')[0] || 'unknown';

  const checkCVStatus = useCallback(async () => {
    try {
      setCheckingStatus(true);
      const response = await fetch(
        `${API_URL}/cv/status?username=${encodeURIComponent(username)}`
      );
      if (response && response.ok) {
        const data = await response.json();
        setCvStatus(data);
      } else {
        setCvStatus({ has_cv: false, message: 'Failed to check CV status' });
      }
    } catch (err) {
      console.error('Error checking CV status:', err);
      setCvStatus({ has_cv: false, message: 'Failed to check CV status' });
    } finally {
      setCheckingStatus(false);
    }
  }, [username]);

  useEffect(() => {
    checkCVStatus();
  }, [checkCVStatus]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!cvText || cvText.trim().length < 10) {
      setError('Please enter your CV content (minimum 10 characters).');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const token = getToken();
      if (!token) {
        setError('No authentication token available. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/cv/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        body: JSON.stringify({
          username: username,
          cv_text: cvText.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to upload CV: ${response.statusText}`
        );
      }

      await response.json();
      setSuccess(true);

      // Reset input
      setCvText('');

      // Refresh CV status
      await checkCVStatus();

      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to upload CV. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-3 sm:p-5 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-6 pb-4 sm:pb-6 border-b-2 border-slate-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-xl sm:rounded-t-2xl">
          <h2 className="text-lg sm:text-2xl font-semibold m-0">
            📄 Upload CV
          </h2>
          <button
            className="bg-white/20 hover:bg-white/30 border-none text-white text-2xl sm:text-3xl w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 leading-none p-0 hover:scale-110"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {/* CV Status Display */}
          {checkingStatus ? (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <div className="w-4 h-4 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin"></div>
                <span>Checking CV status...</span>
              </div>
            </div>
          ) : cvStatus ? (
            <div
              className={`mb-4 p-3 rounded-lg border ${
                cvStatus.has_cv
                  ? 'bg-green-50 border-green-200'
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {cvStatus.has_cv ? (
                  <>
                    <span className="text-green-600 text-lg">✓</span>
                    <div className="flex-1">
                      <p className="text-green-800 font-medium text-sm m-0">
                        CV Uploaded
                      </p>
                      {cvStatus.vector_count !== undefined && (
                        <p className="text-green-600 text-xs m-0 mt-1">
                          {cvStatus.vector_count} sections indexed
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-slate-400 text-lg">○</span>
                    <p className="text-slate-600 text-sm m-0">
                      {cvStatus.message || 'No CV uploaded yet'}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium text-sm m-0">
                ✓ {cvStatus?.has_cv ? 'CV updated' : 'CV uploaded'}{' '}
                successfully! Your CV has been re-indexed and is ready for CV
                Review interviews.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm m-0 whitespace-pre-line">
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Text Input */}
            <div className="mb-4">
              <label
                htmlFor="cv-text"
                className="block mb-2 text-slate-700 font-semibold text-sm sm:text-base"
              >
                CV Content
              </label>
              <textarea
                id="cv-text"
                value={cvText}
                onChange={(e) => setCvText(e.target.value)}
                placeholder="Paste your CV content here..."
                className="w-full min-h-[300px] px-3 py-2.5 sm:py-3 border-2 border-slate-200 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:border-indigo-500 bg-white resize-y"
                disabled={loading}
              />
              <p className="mt-2 text-xs text-slate-500">
                Paste your CV content as text. The system will automatically
                process and index it for CV Review interviews.
              </p>
              {cvText && (
                <div className="mt-2 text-xs text-green-600">
                  {cvText.trim().length} characters entered
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
              <button
                type="submit"
                disabled={loading || !cvText || cvText.trim().length < 10}
                className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:from-indigo-600 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {cvStatus?.has_cv ? 'Updating...' : 'Uploading...'}
                  </span>
                ) : cvStatus?.has_cv ? (
                  'Update CV'
                ) : (
                  'Upload CV'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-slate-200 text-slate-700 rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:bg-slate-300"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>

          {cvStatus?.has_cv && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-xs sm:text-sm m-0">
                <strong>Note:</strong> Uploading a new CV will replace your
                existing CV. Your CV will be re-indexed automatically.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CVUpload;
